import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import LanguageSwitcher from '../../components/layout/LanguageSwitcher';
import Loading from '../../components/layout/Loading';
import './ResetPasswordPage.css';

function ResetPasswordPage({ onPasswordResetSuccess }) {
  const { t, i18n } = useTranslation(['landing', 'shared']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    // Sprawdź czy w URL hash jest token resetu hasła
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      // Supabase automatycznie przetwarza hash URL przy wywołaniu getSession()
      // Musimy to zrobić, żeby Supabase zweryfikował token
      supabase.auth.getSession().then(({ data: { session }, error }) => {
        if (error) {
          console.error('Error processing reset token:', error);
          setError(error.message || t('auth.resetPassword.errorNoToken'));
        } else if (session) {
          // Token został zweryfikowany, możemy pokazać formularz
          setHasToken(true);
        } else {
          // Brak sesji - token może być nieprawidłowy lub wygasły
          setError(t('auth.resetPassword.errorNoToken'));
        }
      });
    } else {
      setError(t('auth.resetPassword.errorNoToken'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // t jest z useTranslation i nie powinno być w dependencies - tłumaczenia ładują się automatycznie

  // Aktualizuj błędy gdy język się zmienia
  useEffect(() => {
    if (error && error.includes('auth.resetPassword.errorNoToken')) {
      // Jeśli error to komunikat o braku tokenu, zaktualizuj go przy zmianie języka
      const hash = window.location.hash;
      if (!hash || !hash.includes('type=recovery')) {
        setError(t('auth.resetPassword.errorNoToken'));
      }
    }
  }, [i18n.language, error, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Walidacja
    if (!password.trim()) {
      setError(t('auth.resetPassword.errorEmpty'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.resetPassword.errorTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.errorMismatch'));
      return;
    }

    setLoading(true);

    try {
      // Supabase automatycznie obsługuje token z hash URL
      // Wystarczy wywołać updateUser z nowym hasłem
      const { error: updateError } = await supabase.auth.updateUser({
        password: password.trim()
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);
      
      // Po 2 sekundach przekieruj do logowania
      setTimeout(() => {
        if (onPasswordResetSuccess) {
          onPasswordResetSuccess();
        } else {
          // Wyczyść hash i przekieruj
          window.location.hash = '';
          window.location.reload();
        }
      }, 2000);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message || t('auth.resetPassword.errorReset'));
    } finally {
      setLoading(false);
    }
  };

  if (!hasToken && !error) {
    return (
      <div className="reset-password-page">
        <header className="reset-password-header">
          <LanguageSwitcher />
        </header>
        <div className="reset-password-container">
          <Loading size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <header className="reset-password-header">
        <LanguageSwitcher />
      </header>

      <div className="reset-password-container">
        <div className="reset-password-card">
          <h1 className="reset-password-title">
            {t('auth.resetPassword.title')}
          </h1>

          {error && (
            <div className="reset-password-error">
              {error}
            </div>
          )}

          {success && (
            <div className="reset-password-success">
              {t('auth.resetPassword.success')}
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="reset-password-form">
              <div className="reset-password-field">
                <label htmlFor="password" className="reset-password-label">
                  {t('auth.resetPassword.newPassword')}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                  className="reset-password-input"
                  disabled={loading || success}
                  required
                  minLength={6}
                />
              </div>

              <div className="reset-password-field">
                <label htmlFor="confirmPassword" className="reset-password-label">
                  {t('auth.resetPassword.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                  className="reset-password-input"
                  disabled={loading || success}
                  required
                  minLength={6}
                />
              </div>

              <div className="reset-password-info">
                {t('auth.resetPassword.info')}
              </div>

              <button
                type="submit"
                className="reset-password-button"
                disabled={loading || success || !password.trim() || !confirmPassword.trim()}
              >
                {loading
                  ? t('auth.resetPassword.resetting')
                  : success
                  ? t('auth.resetPassword.reset')
                  : t('auth.resetPassword.submit')
                }
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;

